import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Input } from '@/components/form/fields/Input/Input';
import { TextArea } from '@/components/form/fields/TextArea/TextArea';
import { RadioGroup } from '@/components/form/fields/RadioGroup/RadioGroup';
import { Notification } from '@/components/Notification/Notification';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { DATE_TIME_FORMAT, formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { prepareResponse, getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { copyToClipboard } from '@/helpers/helpers';
import {
  confirmInvoiceNumberApi,
  openInvoiceLinkApi,
  revealInvoiceLinkApi,
  reviewInvoiceApi,
} from '@/api/v1/invoice-links/invoiceLinkPublicApi';
import { DisputeReasonType, RevealedInvoiceInstructionsType } from '@/types/invoiceVaultTypes';
import { DISPUTE_REASON_OPTIONS } from '@/bundle/_OrgAdmin/pages/InvoiceVaultPage/const/const';
import { InstructionRow, SecureLinkCard, SecureLinkShell } from '@/bundle/pages/SecureLinkPage/styles';
import { useSecureLinkVerification } from '@/bundle/pages/SecureLinkPage/useSecureLinkVerification';
import { VerificationBlock } from '@/bundle/pages/SecureLinkPage/VerificationBlock';
import { Mono } from '@/styles/common';

const DEAD_MESSAGE: Record<string, string> = {
  expired: 'This invoice link has expired. Ask the sender for a new one.',
  revoked: 'This link was revoked by the sender.',
  cancelled: 'This invoice was cancelled by the sender.',
};

export const InvoiceLinkPage = () => {
  const { token } = useParams();
  const [typed, setTyped] = useState('');
  const [decision, setDecision] = useState<'accept' | 'dispute'>('accept');
  const [reason, setReason] = useState<DisputeReasonType>('info');
  const [details, setDetails] = useState('');
  const [error, setError] = useState('');
  const [data, setData] = useState<RevealedInvoiceInstructionsType | null>(null);

  const {
    data: openResponse,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ['invoice_link_open', token],
    queryFn: async () => prepareResponse(await openInvoiceLinkApi(token)),
    retry: false,
  });
  const state = openResponse?.body;
  const v = useSecureLinkVerification(state?.secure_link_token, !!state?.requires_verification);

  const confirm = useMutation({
    mutationFn: async () => prepareResponse(await confirmInvoiceNumberApi(token, typed.trim())),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));
      setError('');
      refetch();
    },
  });
  const review = useMutation({
    mutationFn: async () =>
      prepareResponse(
        await reviewInvoiceApi(
          token,
          decision === 'accept' ? { decision } : { decision, reason, details: details.trim() }
        )
      ),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));
      setError('');
      refetch();
    },
  });
  const reveal = useMutation({
    mutationFn: async () => prepareResponse(await revealInvoiceLinkApi(token)),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));
      setError('');
      setData(response.body);
    },
  });

  if (isPending) {
    return (
      <SecureLinkShell>
        <SecureLinkCard>
          <Typography>Loading...</Typography>
        </SecureLinkCard>
      </SecureLinkShell>
    );
  }

  if (!state || openResponse?.error) {
    return (
      <SecureLinkShell>
        <SecureLinkCard>
          <Typography variant='headline5'>This invoice link cannot be opened.</Typography>
          <Typography variant='bodySmall'>{getResponseError(openResponse?.error) || 'The link is invalid.'}</Typography>
        </SecureLinkCard>
      </SecureLinkShell>
    );
  }

  const dead = DEAD_MESSAGE[state.state];
  const verified = !state.requires_verification || v.verified;
  const disputed = state.review === 'disputed';

  return (
    <SecureLinkShell>
      <SecureLinkCard $maxWidth='760px'>
        <Typography variant='headline4' fontWeight='medium'>
          WireVault secure invoice
        </Typography>
        <Typography variant='bodySmall'>
          <b>{state.organization}</b> sent you an invoice{state.amount_label ? ` for ${state.amount_label}` : ''}
          {state.due_date ? `, due ${formatDate(state.due_date)}` : ''}. The bank instructions come straight from their
          vault, not from the PDF.
        </Typography>
        {state.description && <Typography variant='caption'>{state.description}</Typography>}
        {state.last_resolution && (
          <Notification variant='info'>
            Reply from {state.organization}: {state.last_resolution}
          </Notification>
        )}

        {dead ? (
          <Typography variant='bodySmall'>{dead}</Typography>
        ) : data ? (
          <Instructions data={data} />
        ) : !verified ? (
          <VerificationBlock organization={state.organization} what='the invoice' needsEmailCode={false} v={v} />
        ) : !state.number_confirmed ? (
          <>
            <Typography variant='subTitle'>Which invoice are you paying?</Typography>
            <Typography variant='bodySmall'>
              Type the invoice number printed on the document ({state.invoice_number_hint}).
            </Typography>
            {error && <Notification variant='error'>{error}</Notification>}
            <Box display='flex' gap='12px' alignItems='flex-end'>
              <Input
                name='invoice_number'
                label='Invoice number'
                value={typed}
                placeholder='INV-1050'
                onChange={(e) => setTyped(e.target.value)}
              />
              <ButtonNew
                size='medium'
                disabled={!typed.trim()}
                isLoading={confirm.isPending}
                onClick={() => confirm.mutate()}
              >
                Continue
              </ButtonNew>
            </Box>
          </>
        ) : disputed ? (
          <>
            <Typography variant='subTitle'>Dispute sent</Typography>
            <Typography variant='bodySmall'>
              {state.organization} has been notified ({state.dispute?.reason_label}). The bank instructions stay locked
              until they reply; this page updates when they do.
            </Typography>
          </>
        ) : state.review !== 'accepted' ? (
          <>
            <RadioGroup
              label='Accept or dispute'
              name='decision'
              value={decision}
              onChange={(event) => setDecision(event.target.value as 'accept' | 'dispute')}
              options={[
                { value: 'accept', label: 'The invoice is correct, show me how to pay' },
                { value: 'dispute', label: 'Something is wrong with this invoice' },
              ]}
            />
            {decision === 'dispute' && (
              <Box display='flex' flexDirection='column' gap='8px'>
                <RadioGroup
                  label=''
                  name='reason'
                  value={reason}
                  onChange={(event) => setReason(event.target.value as DisputeReasonType)}
                  options={[...DISPUTE_REASON_OPTIONS]}
                />
                <TextArea
                  name='details'
                  placeholder='Tell them what is wrong'
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  rows={3}
                />
              </Box>
            )}
            {error && <Notification variant='error'>{error}</Notification>}
            <ButtonNew
              size='large'
              isLoading={review.isPending}
              disabled={decision === 'dispute' && details.trim().length < 3}
              onClick={() => review.mutate()}
            >
              {decision === 'accept' ? 'Accept invoice' : 'Send dispute'}
            </ButtonNew>
          </>
        ) : (
          <>
            <Typography variant='subTitle'>Choose how to pay</Typography>
            {error && <Notification variant='error'>{error}</Notification>}
            <Box display='flex' gap='12px' flexDirection='column'>
              <ButtonNew size='large' isLoading={reveal.isPending} onClick={() => reveal.mutate()}>
                View bank instructions
              </ButtonNew>
              <ButtonNew size='large' variant='secondary' disabled={!state.pay_through_wirevault}>
                Pay through WireVault{state.pay_through_wirevault ? '' : ' (not enabled by the sender)'}
              </ButtonNew>
            </Box>
          </>
        )}
        <Typography variant='caption' fontWeight='medium' textAlign='right'>
          Expires {formatDate(state.expires_at, DATE_TIME_FORMAT)}
        </Typography>
      </SecureLinkCard>
    </SecureLinkShell>
  );
};

const Instructions = ({ data }: { data: RevealedInvoiceInstructionsType }) => (
  <>
    <Typography variant='subTitle'>
      {data.rail_label} instructions for invoice {data.invoice_number}
      {data.amount_label ? ` · ${data.amount_label}` : ''}
    </Typography>
    <Box display='flex' flexDirection='column'>
      {data.fields.map((f) => (
        <InstructionRow key={f.key}>
          <Typography variant='bodySmall'>{f.label}</Typography>
          <Mono>{f.value}</Mono>
          <ButtonNew
            size='medium'
            variant='secondary'
            onClick={() => copyToClipboard(f.value).then(() => showSnackbar(`${f.label} copied.`))}
          >
            Copy
          </ButtonNew>
        </InstructionRow>
      ))}
    </Box>
    <Typography variant='caption'>
      Record v{data.record.version}, hash {data.record.hash.slice(0, 16)}…, anchored as {data.record.blockchain_id} on{' '}
      {formatDate(data.record.saved_at, DATE_TIME_FORMAT)}. Shared by {data.shared_by}. If anything you were told
      differs from this page, do not pay and contact the sender.
    </Typography>
  </>
);
